import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Wrench, Smartphone, Zap, ArrowRight } from 'lucide-react';

const HowItWorksPage = () => {
  const { t } = useLanguage();

  const steps = [
    {
      num: '01',
      icon: Wrench,
      title: t('how_step_1'),
      titleZh: '安裝',
      desc: t('how_step_1_desc'),
      descZh: '將智慧閥門連接到您的灌溉系統',
      details: [
        'Connect valve to your pipe / 將閥門連接到管道',
        'Attach DTU controller / 安裝 DTU 控制器',
        'Power on the system / 啟動系統電源'
      ]
    },
    {
      num: '02',
      icon: Smartphone,
      title: t('how_step_2'),
      titleZh: '連接',
      desc: t('how_step_2_desc'),
      descZh: '在應用程式中輸入設備序號進行配對',
      details: [
        'Download the app / 下載應用程式',
        'Create your account / 建立帳號',
        'Enter device serial number / 輸入設備序號'
      ]
    },
    {
      num: '03',
      icon: Zap,
      title: t('how_step_3'),
      titleZh: '控制',
      desc: t('how_step_3_desc'),
      descZh: '隨時隨地管理您的灌溉系統',
      details: [
        'Open/close valve remotely / 遠端開關閥門',
        'Set irrigation schedules / 設定灌溉排程',
        'Monitor device status / 監控設備狀態'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-fac-dark pt-16">
      {/* Hero */}
      <section className="py-20 relative" data-testid="how-hero">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <h1 className="font-heading font-black text-5xl md:text-6xl text-white tracking-tighter uppercase mb-4">
              {t('how_title')}
            </h1>
            <p className="text-2xl text-fac-green font-chinese">
              運作方式
            </p>
          </div>

          {/* Steps */}
          <div className="space-y-16">
            {steps.map((step, index) => (
              <div 
                key={index}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`how-step-${index + 1}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-4">
                    <span className="font-heading font-black text-6xl text-fac-green/30">
                      {step.num}
                    </span>
                    <div className="w-16 h-16 rounded-lg bg-fac-green/10 flex items-center justify-center">
                      <step.icon className="w-8 h-8 text-fac-green" />
                    </div>
                  </div>
                  
                  <h2 className="font-heading font-bold text-3xl text-white uppercase mb-2">
                    {step.title}
                  </h2>
                  <p className="text-xl text-fac-green font-chinese mb-4">
                    {step.titleZh}
                  </p>
                  <p className="text-lg text-gray-300 mb-6 font-chinese">
                    {step.desc}
                    <br />
                    {step.descZh}
                  </p>

                  <ul className="space-y-3">
                    {step.details.map((detail, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <ArrowRight className="w-5 h-5 text-fac-green flex-shrink-0" />
                        <span className="text-muted-foreground font-chinese">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="bg-fac-card border border-border rounded-lg p-8 cyber-border">
                    <div className="aspect-video bg-fac-paper rounded-lg flex items-center justify-center">
                      <step.icon className="w-24 h-24 text-fac-green/30" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-fac-paper" data-testid="how-cta">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading font-black text-4xl text-white tracking-tighter uppercase mb-4">
            SIMPLE AND EASY
          </h2>
          <p className="text-xl text-fac-green font-chinese mb-8">
            簡單易用
          </p>
          <p className="text-lg text-gray-300 mb-8 font-chinese">
            No technical knowledge required. If you can use a smartphone, you can use FAC Smart Valve.
            <br />
            無需技術知識。只要會用智慧手機，就會用 FAC 智慧閥門。
          </p>
          <Link to="/login">
            <Button 
              size="lg"
              className="btn-skew bg-fac-green text-black font-bold uppercase tracking-wider hover:bg-fac-green-dim h-14 px-8"
              data-testid="how-cta-btn"
            >
              <span>TRY IT NOW / 立即試用</span>
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HowItWorksPage;
